import * as React from 'react';
import { observable, computed, action, transaction, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Button, ButtonGroup } from 'reactstrap';

import { KeyboardKeys } from '@app/AppConstants/KeyboardKeys';
import { ApiUrls } from '@app/AppConstants/ApiUrls';
import { ApiClient, PersonCommentCreateDto, PersonCommentReadDto, PersonCommentUpdateDto } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import { BaseFormModel } from '@app/Services/BaseFormModel';
import { displayName, isDate, isDateAfter, isDateBefore, isRequired } from '@app/Services/Validation';

import { Icon } from '@app/Components/Icon';
import { FormCheckbox, FormInput } from '@app/Components/FormControls';
import { DatePickerValidatedControl } from '@app/Components/DateTimeControls';
import DateTimeService from '@app/Services/DateTimeService';
import { modalService, ModalDialogOptions, ModalWindow, IModalDialogContent, ModalButtonType } from '../Modal';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import HistorizationStore from '@app/Stores/HistorizationStore';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';
import { IPersonStore } from '../VirtualizedRoster';
import { AzureAuth, UserPermissions, WritePermissionOptions } from '../AzureAuth';
import { applyDecorators } from "@app/Helpers/Decorator";

type PersonCommentDialogProps = {
    personId: string;
    date: Date;
    personStore: IPersonStore;
    historizationStore?: HistorizationStore;
};

type PersonCommentDto = Omit<PersonCommentReadDto, 'personId'>;

@observer
export default class PersonCommentDialog extends React.Component<PersonCommentDialogProps> implements IModalDialogContent<void> {
    _store: PersonCommentDialogStore;

    constructor(props: PersonCommentDialogProps) {
        super(props);
        applyDecorators(this);
        this._store = new PersonCommentDialogStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        return {
            buttons: [
                {
                    type: ModalButtonType.Close,
                    onClick: () => {
                        window.close();
                    }
                }
            ],
            loader: this._store.loader,
            width: '1000px',
            bodyClassName: this._store.currentComment ? 'inline-comment-fade' : '',
            footerPrefix: <FormCheckbox name="showHistoryComment" checked={this._store.showHistoryComment} className="mr-4" label="Show all comments" changeHandler={this._store.toggleHistory} />
        };
    }

    async componentDidMount() {
        await this._store.loadCurrentPerson();
        await this._store.loadItems();
    }

    render() {
        const hasAccessToAddNew = this._store.hasWritePermission(this.props.date);
        return (
            <>
                {this._store.comments.map((comment) => this.renderCommentItem(comment))}
                <Button size="sm" className="inline-comment-add" hidden={this._store.isNewComment || !hasAccessToAddNew} onClick={this._store.showForm}>
                    Add new
                </Button>
                {this._store.currentComment && !this._store.currentComment.commentId && <PersonCommentForm store={this._store} />}
            </>
        );
    }

    @action
    renderCommentItem(comment: PersonCommentReadDto) {
        const canEdit = this._store.hasWritePermission(comment.validFromDate);
        if (this._store.currentComment && this._store.currentComment.commentId === comment.commentId && canEdit) {
            return <PersonCommentForm key={this._store.currentComment.commentId || 'comment_new'} store={this._store} />;
        } else if (comment.commentId) {
            let className = "inline-comment-item";
            let title = '';
            if (!canEdit) {
                className += " disabled";
                title = "This date is disabled for editing";
            }
            return (
                <div key={'comment_' + comment.commentId} title={title} className={className} onClick={this._store.editComment(comment.commentId)}>
                    <div className="inline-comment-text">{comment.text}</div>
                    {canEdit && (
                        <Button className="inline-comment-remove" outline color="danger" size="sm" onClick={this._store.deleteComment(comment.commentId)}>
                            <Icon name="trash-alt" />
                        </Button>
                    )}
                    <div className="inline-comment-info left">
                        <div className="inline-comment-info-item">
                            From {DateTimeService.toUiDate(comment.validFromDate)}
                            {comment.validToDate ? ' till ' + DateTimeService.toUiDate(comment.validToDate) : ''}
                        </div>
                    </div>
                    <div className="inline-comment-info">
                        {comment.isVisibleByCrewMember && <div className="inline-comment-info-item crewVisible" title="Visible for person and people with special permission">Visible for Employee</div>}
                        {comment.isVisibleToAllUsers && <div className="inline-comment-info-item visibleToAll" title="Visible for everyone including the person and colleguaes">Public</div>}
                        {comment.isStandby && <div className="inline-comment-info-item standby" title="For standby list">Standby</div>}
                        <div className="inline-comment-info-item" title={comment.modifiedAtDateUtc ? DateTimeService.toUiDateTime(comment.modifiedAtDateUtc) : ''}>
                            {comment.modifiedBy}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    }
}
type PersonCommentFormProps = {
    store: PersonCommentDialogStore
}
@observer
class PersonCommentForm extends React.Component<PersonCommentFormProps> {
    commentRef: React.RefObject<HTMLInputElement> = React.createRef();
    
    constructor (props:PersonCommentFormProps) {
        super(props);
        applyDecorators(this);
    }

    componentDidMount() {
        const comment = this.commentRef.current;
        if (comment) window.setTimeout(() => comment.focus(), 0);
        document.addEventListener('click', this._handleClick, { capture: true });
    }

    componentWillUnmount() {
        document.removeEventListener('click', this._handleClick, { capture: true });
    }

    private _handleClick = (event: MouseEvent) => {
        const { store } = this.props;
        const target = event.target as HTMLDivElement;
        if (!target || target.closest('.inline-comment') || target.closest('#datetimepicker-container') || !target.parentNode) return;
        if (store.currentComment) {
            const parent = target.parentElement;
            if (store.currentComment.text && !parent?.closest('.modal-footer') && !parent?.closest('.close')) {
                void store.saveData();
            } else {
                store.resetForm();
            }
        }
    };

    handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { store } = this.props;
        if (event.keyCode === KeyboardKeys.ENTER) {
            void store.saveData();
        }
        if (event.keyCode === KeyboardKeys.ESCAPE) {
            event.stopPropagation();
            store.resetForm();
        }
    };

    render() {
        const { store } = this.props;

        if (!store.currentComment) return null;

        const startOfMonth = DateTimeService.toFirstDayOfMonth(store.currentComment.validFromDate);
        const canSave = store.hasWritePermission(store.currentComment.validFromDate);
        return (
            <div className="inline-comment">
                <div className="inline-comment-form">
                    <div className="inline-comment-field">
                        <FormInput
                            formModel={store.currentComment}
                            name="text"
                            className="form-control-sm comment"
                            autoComplete="off"
                            onKeyUp={this.handleKeyUp}
                            maxLength={255}
                            innerRef={this.commentRef}
                        />
                    </div>
                    <div className="inline-comment-actions" style={{ width: '135px' }}>
                        <DatePickerValidatedControl formModel={store.currentComment} name="dateFrom" minDate={startOfMonth} />
                    </div>
                    <div className="inline-comment-actions" style={{ width: '135px' }}>
                        <DatePickerValidatedControl formModel={store.currentComment} name="dateTo" minDate={startOfMonth} />
                    </div>
                    <div className="inline-comment-actions">
                        <ButtonGroup className="checkbox-group">
                            {store.isFcm && <Button size="sm" className={store.currentComment.isStandby ? 'active' : ''} onClick={store.toggleStandby} title="For standby list">
                                Standby
                            </Button>}
                            <Button size="sm" className={store.currentComment.isVisibleByCrewMember ? 'active' : ''} onClick={store.toggleVisibleByCrewMember} title="Visible for person and people with special permission">
                                Visible for Employee
                            </Button>
                            {store.isGround && <Button size="sm" className={store.currentComment.isVisibleToAllUsers ? 'active' : ''} onClick={store.toggleVisibleToAllUsers} title="Visible for everyone including the person and colleguaes">
                                Public
                            </Button>}
                        </ButtonGroup>
                    </div>
                    <div className="inline-comment-controls">
                        <Button size="sm" onClick={store.resetForm}>
                            <Icon name="times" />
                        </Button>
                        <Button size="sm" onClick={store.saveData} disabled={!canSave} title={canSave ? '' : `You don't have access`}>
                            <Icon name="check" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

class PersonCommentDialogStore {
    private _apiClient: ApiClient = new ApiClient();
    @observable comments: PersonCommentReadDto[] = [];
    @observable currentComment: FormModel | null = null;
    @observable showHistoryComment: boolean = false;
    @observable currentPersonId: string | undefined = undefined;
    public loader: PromiseCompletion = new PromiseCompletion();

    private readonly _personId: string;
    private readonly _date: Date;
    private readonly _historizationStore?: HistorizationStore;
    private readonly _personStore: IPersonStore;

    constructor(props: PersonCommentDialogProps) {
        applyDecorators(this);
        this._personId = props.personId;
        this._date = props.date;
        this._historizationStore = props.historizationStore;
        this._personStore = props.personStore;
        this.resetForm();
    }

    @computed
    public get isFcm() {
        const personDetails = this._personStore?.getDetails(this._personId);
        const isFcm = personDetails?.function?.functionGroupId === FunctionGroupIds.Cockpit || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Cockpit
            || personDetails?.function?.functionGroupId === FunctionGroupIds.Cabin || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Cabin;

        return isFcm;
    }

    @computed
    public get isGround() {
        const personDetails = this._personStore?.getDetails(this._personId);
        const isGround = personDetails?.function?.functionGroupId === FunctionGroupIds.Ground || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Ground;

        return isGround;
    }

    @computed
    public get canReadEmployeeComments() {
        const personDetails = this._personStore?.getDetails(this._personId);
        const isCockpit = personDetails?.function?.functionGroupId === FunctionGroupIds.Cockpit || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Cockpit;
        const isCabin = personDetails?.function?.functionGroupId === FunctionGroupIds.Cabin || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Cabin;
        const isGround = personDetails?.function?.functionGroupId === FunctionGroupIds.Ground || personDetails?.function?.planningFunctionGroupId === FunctionGroupIds.Ground;

        if (isCockpit && AzureAuth.hasAccess(UserPermissions.CommentEmployeeCockpitRead))
            return true;

        if (isCabin && AzureAuth.hasAccess(UserPermissions.CommentEmployeeCabinRead))
            return true;

        if (isGround && AzureAuth.hasAccess(UserPermissions.CommentEmployeeGroundRead))
            return true;

        return false;
    }

    @action
    async loadItems() {
        const params = {
            personId: this._personId,
            date: this._date,
            showAllComments: this.showHistoryComment
        };
        const { data } = await ApiService.getTypedData<PersonCommentReadDto[]>(ApiUrls.PersonCommentUrl, params, { completion: this.loader });

        runInAction(() => {
            this.comments = data;
        });
    }

    @action
    async loadCurrentPerson() {
        const { data } = await this._apiClient.personRelatedPersonGet({ completion: this.loader });
        runInAction(() => {
            this.currentPersonId = data.currentPerson?.id;
        });
    }

    @action
    showForm = () => {
        this.currentComment = new FormModel();
        if (!this.canReadEmployeeComments){
            this.currentComment.isVisibleByCrewMember = true;
        }
    };

    private reloadPersonInfo() {
        this._personStore.loadPersonInfo(this._personId);
    }

    async createComment(comment: PersonCommentDto) {
        const params = new PersonCommentCreateDto({
            personId: this._personId,
            text: comment.text,
            validFromDate: comment.validFromDate,
            validToDate: comment.validToDate,
            isStandby: comment.isStandby,
            isVisibleByCrewMember: comment.isVisibleByCrewMember,
            isVisibleToAllUsers: comment.isVisibleToAllUsers
        });

        await ApiService.postTypedData<void>(ApiUrls.PersonCommentUrl, params, { completion: this.loader });
        await this.loadItems();

        this.reloadPersonInfo();
        this.resetForm();
    }

    async updateComment(comment: PersonCommentDto) {
        const params = new PersonCommentUpdateDto({
            personId: this._personId,
            commentId: comment.commentId || '',
            text: comment.text,
            validFromDate: comment.validFromDate,
            validToDate: comment.validToDate,
            isStandby: comment.isStandby,
            isVisibleByCrewMember: comment.isVisibleByCrewMember,
            isVisibleToAllUsers: comment.isVisibleToAllUsers
        });
        const originalComment = this.comments.find((el) => el.commentId === comment.commentId);
        if (originalComment && (originalComment.text !== comment.text || originalComment.validFromDate !== comment.validFromDate || originalComment.validToDate !== comment.validToDate || originalComment.isStandby !== comment.isStandby || originalComment.isVisibleByCrewMember !== comment.isVisibleByCrewMember)) {
            await ApiService.putTypedData<void>(ApiUrls.PersonCommentUrl, params, { completion: this.loader });
            await this.loadItems();
        }
        this.reloadPersonInfo();
        this.resetForm();
    }

    @action.bound
    async saveData() {
        const isValidComment = this.currentComment?.validate();
        if (this.currentComment && isValidComment) {
            if (!this.currentComment.commentId) {
                await this.createComment(this.currentComment);
            } else {
                await this.updateComment(this.currentComment);
            }
        }
    }

    @action
    deleteComment = (commentId: string) => async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!(await modalService.showConfirmation('Are you sure you want to delete this comment?', 'Warning', { color: 'warning' }))) {
            return;
        }

        await ApiService.deleteData(ApiUrls.CommentUrl + '?id=' + commentId, { completion: this.loader });
        await this.loadItems();
        this.reloadPersonInfo();
    };

    @action
    editComment = (commentId: string) => () => {
        const comment = this.comments.find((item: PersonCommentReadDto) => item.commentId === commentId);

        if (comment && !this.hasWritePermission(comment.validFromDate)) return;

        comment &&
            transaction(() => {
                this.currentComment = new FormModel(comment);
            });
    };

    @action
    toggleHistory = () => {
        this.showHistoryComment = !this.showHistoryComment;
        void this.loadItems();
    };

    @action
    toggleStandby = () => {
        if (this.currentComment) {
            this.currentComment.isStandby = !this.currentComment.isStandby;
        }
    };

    @action
    toggleVisibleByCrewMember = () => {
        if (this.currentComment) {
            if (this.currentComment.isVisibleByCrewMember && !this.canReadEmployeeComments){
                return;
            }
            this.currentComment.isVisibleByCrewMember = !this.currentComment.isVisibleByCrewMember;
            if (this.currentComment.isVisibleByCrewMember){
                this.currentComment.isVisibleToAllUsers = false;
            }
        }
    };

    @action
    toggleVisibleToAllUsers = () => {
        if (this.currentComment) {
            if (this.currentComment.isVisibleToAllUsers && !this.canReadEmployeeComments){
                return;
            }
            this.currentComment.isVisibleToAllUsers = !this.currentComment.isVisibleToAllUsers;
            if (this.currentComment.isVisibleToAllUsers){
                this.currentComment.isVisibleByCrewMember = false;
            }
        }
    };

    @action
    resetForm = () => {
        this.currentComment = null;
    };

    @computed
    get isNewComment() {
        return this.currentComment && !this.currentComment.commentId;
    }

    hasWritePermission(date: Date) {
        const data = [{ date, person: { id: this._personId, code: '' } }];

        const personFunction = this._personStore.getDetails(this._personId)?.function;
        const options: WritePermissionOptions = {
            type: 'WritePermissionOptions',
            ignorePermissions: [UserPermissions.RosterGroundWriteTracking],
            personStore: this._personStore
        };

        const hasWritePermission = this._historizationStore?.hasWritePermission(data, options, personFunction?.functionGroupId || null, true)
            || this._historizationStore?.hasWritePermission(data, options, personFunction?.planningFunctionGroupId || null, true);
            
        return hasWritePermission || (this._personId === this.currentPersonId);
    }
}

class FormModel extends BaseFormModel implements PersonCommentDto {
    @observable
    @displayName('Text')
    @isRequired()
    text: string = '';

    @observable
    @displayName('Date From')
    @isRequired()
    @isDate()
    @isDateBefore('dateTo')
    dateFrom: string = '';

    @observable
    @displayName('Date To')
    @isDate()
    @isDateAfter('dateFrom')
    dateTo: string = '';

    @observable isStandby: boolean = false;
    @observable isVisibleByCrewMember: boolean = false;
    @observable isVisibleToAllUsers: boolean = false;

    @observable commentId?: string = undefined;
    @observable modifiedAtDateUtc?: Date = undefined;
    @observable modifiedBy: string = '';
    @observable createdAtDateUtc?: Date = undefined;
    @observable createdBy: string = '';

    @computed
    get validFromDate() {
        return DateTimeService.parseUiDate(this.dateFrom);
    }
    @computed
    get validToDate() {
        return this.dateTo ? DateTimeService.parseUiDate(this.dateTo) : undefined;
    }

    constructor(comment?: PersonCommentReadDto) {
        super();
        applyDecorators(this);
        if (comment) {
            this.text = comment.text;
            this.dateFrom = DateTimeService.toUiDate(comment.validFromDate);
            this.dateTo = comment.validToDate ? DateTimeService.toUiDate(comment.validToDate) : '';
            this.isStandby = comment.isStandby;
            this.isVisibleByCrewMember = comment.isVisibleByCrewMember;
            this.isVisibleToAllUsers = comment.isVisibleToAllUsers;
            this.commentId = comment.commentId;
            this.modifiedAtDateUtc = comment.modifiedAtDateUtc;
            this.modifiedBy = comment.modifiedBy;
            this.createdAtDateUtc = comment.createdAtDateUtc;
            this.createdBy = comment.createdBy;
        } else {
            const today = DateTimeService.today();
            this.dateFrom = DateTimeService.toUiDate(today);
        }
    }
}